import AppBar from '@material-ui/core/AppBar';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper/Popper';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import withStyles, { WithStyles } from '@material-ui/core/styles/withStyles';
import Toolbar from '@material-ui/core/Toolbar';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import MoreVert from '@material-ui/icons/MoreVert';
import { useTheme } from '@material-ui/styles';
import createStyles from '@material-ui/styles/createStyles';
import React, { useState } from 'react';
import { IConfig, IDocument, ISigner } from '../store/types';
import { colors } from '../theme';
import SubmissionInteractions from './SubmissionInteractions';
import { MD_MEDIA_QUERY } from '../common/constants';
import { checkIfSignerHasSettingsSaved } from '../common/utilities';

const styles = (theme: Theme) =>
  createStyles({
    '@global .ds--footer': {
      position: 'fixed',
      top: 'auto',
      bottom: 0,
    },
    toolbar: {
      height: '80px',
      justifyContent: 'space-between',
    },
    legalLinks: {
      display: 'inline-block',
      marginLeft: theme.spacing(3),
    },
    anchor: {
      color: colors.dusk,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline',
      },
      fontSize: 13,
    },
    spacer: {
      padding: '0 12px',
      fontSize: 13,
      display: 'inline-block',
    },
    actions: {
      color: colors.bluePrimary,
    },
    rightSide: {
      display: 'flex',
      color: colors.dusk,
      alignItems: 'center',
    },
    rideSideMdScreen: {
      display: 'flex',
      alignItems: 'center',
      width: '100%',
      justifyContent: 'center',
      maxHeight: '44px',
    },
    fieldsText: {
      marginLeft: theme.spacing(),
      marginRight: theme.spacing(2),
    },
    menuStyle: {
      zIndex: 10000,
      top: '-2px !important',
      borderRadius: 0,
      marginLeft: '9px',
    },
    responsiveSubmitBtn: {
      width: '60%',
      whiteSpace: 'pre',
      '&:active': {
        backgroundColor: '#1537BB',
      },
    },
  });

interface IBottomBarProps extends WithStyles<typeof styles> {
  requiredFieldsSigned: number;
  requiredFieldsTotal: number;
  submitEnvelope: () => void;
  exitSigning: () => void;
  addEventListener: () => void;
  removeEventListener: () => void;
  completed: boolean;
  alreadySigned: boolean;
  termsAccepted: boolean;
  signingActionsMenuOpen: boolean;
  envelopeId: string;
  documents: IDocument[];
  downloadEnvelopeFile: (downloadType: string) => void;
  config: IConfig;
  scrollToNextBlock: () => void;
  currentBlockIndex: number | null;
  signer: ISigner;
  showEditSignatureModal: () => void;
  signatureAccepted?: boolean;
}

const BottomBar = (props: IBottomBarProps) => {
  const theme = useTheme<Theme>();
  const bottomBarRef: any = React.createRef<HTMLDivElement>();
  const {
    classes,
    alreadySigned,
    downloadEnvelopeFile,
    config,
    currentBlockIndex,
    completed,
    scrollToNextBlock,
    signer,
    showEditSignatureModal,
    signatureAccepted,
  } = props;
  const screenWidthIsLessThanOrEqualToMdBreakpoint = useMediaQuery(MD_MEDIA_QUERY);
  const areLegalLinksVisible = config && config.areLegalLinksVisible ? config.areLegalLinksVisible : false;
  const isMeSigner = signer?.title === 'Me';
  const signerHasSignerSettingsSaved = checkIfSignerHasSettingsSaved(signer?.signingSettings);

  const [state, setState] = useState({
    anchorEl: null,
  });

  let isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  isFullScreen = isFullScreen ? true : false;

  const isMultipleDocs = props.documents.length > 1;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setState({
      anchorEl: event.currentTarget as any,
    });
  };

  const handleClose = () => {
    setState({
      anchorEl: null,
    });
  };

  const handleExitSigning = () => {
    handleClose();
    props.exitSigning();
  };

  const handleButtonClick = (e: any) => {
    if (screenWidthIsLessThanOrEqualToMdBreakpoint && !completed) {
      return scrollToNextBlock();
    }
    return props.submitEnvelope();
  };

  const handleOnTouch = (e: any) => {
    if (screenWidthIsLessThanOrEqualToMdBreakpoint && !completed) {
      e.preventDefault();
      return scrollToNextBlock();
    } else if (screenWidthIsLessThanOrEqualToMdBreakpoint && completed) {
      e.preventDefault();
      return props.submitEnvelope();
    }
    return;
  };

  const handleLinkClick = (url: string) => window.open(url, '_blank');
  const showEditSignatureOption = isMeSigner && signer.signingStatus !== 'Complete' && (signerHasSignerSettingsSaved || signatureAccepted);  
  const SigningActionsItems = [
    showEditSignatureOption ? <MenuItem data-spec='edit-signer-signature' key={0} onClick={() => showEditSignatureModal()}>
      Edit Signature
    </MenuItem> : null,
    <MenuItem key={0} onClick={() => downloadEnvelopeFile('pdf')}>
      {isMultipleDocs ? 'Download Single PDF' : 'Download PDF'}
    </MenuItem>,
    isMultipleDocs ? (
      <MenuItem key={1} onClick={() => downloadEnvelopeFile('zip')}>
        Download Individual PDFs (Zip)
      </MenuItem>
    ) : null,
    !alreadySigned ? (
      <MenuItem key={2} onClick={handleExitSigning}>
        Exit signing
      </MenuItem>
    ) : null,
    screenWidthIsLessThanOrEqualToMdBreakpoint ? (
      <>
        <MenuItem key={3} onClick={() => handleLinkClick('https://support.skyslope.com/hc/en-us')}>
          Support
        </MenuItem>
        <MenuItem key={4} onClick={() => handleLinkClick('https://skyslope.com/terms-conditions/')}>
          Terms
        </MenuItem>
        <MenuItem key={5} onClick={() => handleLinkClick('https://skyslope.com/privacy-policy/')}>
          Privacy
        </MenuItem>
      </>
    ) : null,
  ];

  return (
    <AppBar className="ds--footer" position="fixed" ref={bottomBarRef}>
      <Toolbar className={classes.toolbar}>
        <div>
          <Button data-spec="signing-actions" aria-controls="simple-menu" aria-haspopup="true" onClick={handleClick} className={classes.actions}>
            {isFullScreen ? (
              'Actions'
            ) : (
              <>
                {' '}
                Signing Actions <MoreVert />{' '}
              </>
            )}
          </Button>
          {areLegalLinksVisible && !isFullScreen ? (
            <div className={classes.legalLinks}>
              <a className={classes.anchor} href="https://support.skyslope.com/hc/en-us" target="blank">
                Support
              </a>
              <span className={classes.spacer}>|</span>
              <a className={classes.anchor} href="https://skyslope.com/terms-conditions/" target="blank">
                Terms
              </a>
              <span className={classes.spacer}>|</span>
              <a className={classes.anchor} href="https://skyslope.com/privacy-policy/" target="blank">
                Privacy
              </a>
            </div>
          ) : (
            ''
          )}
          <Popper
            open={Boolean(state.anchorEl)}
            anchorEl={state.anchorEl}
            className={classes.menuStyle}
            placement="top-start"
          >
            <Paper className={classes.menuStyle}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>{SigningActionsItems}</MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
        </div>
        <SubmissionInteractions
          termsAccepted={props.termsAccepted}
          completed={completed}
          alreadySigned={alreadySigned}
          screenWidthIsLessThanOrEqualToMdBreakpoint={screenWidthIsLessThanOrEqualToMdBreakpoint}
          requiredFieldsSigned={props.requiredFieldsSigned}
          requiredFieldsTotal={props.requiredFieldsTotal}
          currentBlockIndex={currentBlockIndex}
          onMouseUp={handleButtonClick}
          onTouchEnd={handleOnTouch}
          classes={classes}
        />
      </Toolbar>
    </AppBar>
  );
};

export default withStyles(styles)(BottomBar);
